/**
 * `InstallmentPaymentDisplay` renders monthly instalment pricing.
 *
 * Behaviour:
 * - When first and subsequent instalment amounts are equal, renders
 *   "N payments of £X.XX /month".
 * - When they differ, renders "1 payment of £X.XX followed by N payments of
 *   £Y.YY /month".
 * - Formats amounts using the provided `culture` and current currency symbol.
 * - Supports optional leading `text` (e.g. "or ") and applies a CSS class via
 *   `style`.
 */
import React from 'react';
import { formatNumber } from '../../../common/Formatter';
import { useCurrencySymbol } from '../../../../hooks/useCurrencySymbol';
import { InstallmentPrice } from '../../../common/ProductMethods';
import { useTranslation } from 'react-i18next';

type InstallmentPaymentDisplayProps = {
  style: string;
  text: string;
  priceMonthly: InstallmentPrice;
  culture: string;
};

const InstallmentPaymentDisplay = ({
  style,
  text,
  priceMonthly,
  culture
}: InstallmentPaymentDisplayProps): JSX.Element => {
  const { t } = useTranslation();
  const installmentPerMonthText = t(
    'renewals-quote.installment-per-month-text'
  );
  const installmentsPaymentsOfText = t(
    'renewals-quote.installments-payments-of-text'
  );
  const currencySymbol = useCurrencySymbol();
  return (
    <>
      {priceMonthly.subsequentInstallmentPrice ===
        priceMonthly.firstInstallmentPrice && (
        <p>
          <span>
            {' '}
            {priceMonthly.totalNumberOfInstallments}
            {installmentsPaymentsOfText}
          </span>
          <span className={style}>
            {`${text}${currencySymbol}${formatNumber(
              priceMonthly.firstInstallmentPrice,
              2,
              culture
            )}`}{' '}
          </span>
          <span>{installmentPerMonthText}</span>
        </p>
      )}
      {priceMonthly.subsequentInstallmentPrice !==
        priceMonthly.firstInstallmentPrice && (
        <p>
          <span>{t('renewals-quote.monthly-one-payment-text')}</span>
          <span className={style}>
            {`${text}${currencySymbol}${formatNumber(
              priceMonthly.firstInstallmentPrice,
              2,
              culture
            )}`}
          </span>
          <span>
            {t('renewals-quote.monthly-pay-followed-by-text', {
              noOfInstallments: priceMonthly.totalNumberOfInstallments - 1
            })}
          </span>
          <span className={style}>
            {`${text}${currencySymbol}${formatNumber(
              priceMonthly.subsequentInstallmentPrice,
              2,
              culture
            )}`}{' '}
          </span>
          <span>{installmentPerMonthText}</span>
        </p>
      )}
    </>
  );
};

export default InstallmentPaymentDisplay;
